import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Route, Routes, useNavigate, useParams} from "react-router-dom";
import {Helmet} from "react-helmet";
import './movieDetails.css';
import TmdbScore from "../../tmdbScore/tmdbScore";
import {Spinner} from "../../../shared";
import {useSelector} from "react-redux";
import {useIntl} from "react-intl";
import {cn, getViewMessage, getYearOfDate} from "../../../../utils/utils";
import users from "../../../users";

import '../serieDetails/serieDetails.scss';
import useScrollContainerRef from "../utils/useScrollContainerRef";
import ButtonV2 from "../../../shared/button-v2/button-v2";
import TmdbCollectionSelector from "../tmdbCollectionSelector/tmdbCollectionSelector";
import TmdbViewLater from "../tmdbViewLater/TmdbViewLater";
import TmdbNotepad from "../tmdbNotepad/tmdbNotepad";
import OverviewCardV2 from "../../overviewCardV2/overviewCardV2";
import TmdbItemSuggestions from "../tmdb-item-suggestions/tmdb-item-suggestions";
import TmdbImage from "../../../shared/tmdb-image/tmdb-image";
import TmdbViewToggle from "../tmdbViewToggle/tmdb-view-toggle";
import TmdbLikeItem from "../tmdbLikeItem/tmdb-like-item";
import TmdbItemReviews from "../tmdb-item-reviews/tmdb-item-reviews";
import useMovieDetailsData from "../../hooks/useMovieDetailsData";

function MovieDetails({squadflixTvHook}) {

    const intl = useIntl();
    const navigate = useNavigate();
    const {id} = useParams();
    const [playing] = useState(false);
    const darkTheme = useSelector(users.selectors.isDarkMode);
    const containerRef = useRef(null);
    const [backdropOpacity, setBackdropOpacity] = useState(0);
    const {
        movie,
        logoImage,
        backgroundImage,
        backgroundAspectRatio,
        from
    } = useMovieDetailsData(id, squadflixTvHook);

    const parentRef = useScrollContainerRef();
    const [selectedRoute, setSelectedRoute] = useState("reviews");
    const routes = [{
        route: "reviews",
        name: "Reseñas",
    }, {
        route: "suggestions",
        name: "Sugerencias",
    }, {
        route: "details",
        name: "Detalles",
    }, {
        onClick: () => {
            navigate("#notes")
        },
        name: "Mis Notas",
    }]

    useEffect(() => {
        setSelectedRoute("reviews");
    }, [id]);

    useLayoutEffect(() => {
        if (!parentRef.ref) {
            return;
        }

        parentRef.ref.current.addEventListener("scroll", () => {
            const scrollTop = parentRef.ref.current.scrollTop;
            const newOpacity = Math.min(scrollTop / 300, 0.6);
            setBackdropOpacity(newOpacity);
        });
    }, [parentRef.ref]);

    if (!movie) {
        return (
            <center className="mt-4">
                <Spinner></Spinner>
            </center>
        )
    }

    return (
        <div className={cn("serie-details", {"serie-details-dark": darkTheme})} ref={containerRef}>
            <Helmet>
                <meta charSet="utf-8"/>
                <title>{movie.title}</title>
                <link rel="icon"
                      href="https://torresowo.myftp.org/web/cap17an/cap17drive/imagenes/Icono-Cap17Drive.png"/>
            </Helmet>
            <div className="serie-details__top-header" style={{
                backgroundColor: backdropOpacity > 0.55 ? (darkTheme ? "rgb(33,37,41)" : "white") : "transparent",
                width: parentRef.width,
            }}>
                <ButtonV2
                    className="serie-details__back-button"
                    width={(from.customKey && backdropOpacity <= 0.55) ? "fit-content" : "50px"}
                    onClick={() => {
                        navigate(from.pathName)
                    }}>
                    <i className={cn("fa-solid fa-chevron-left fa-lg", {"pe-3": backdropOpacity <= 0.55 && from.customKey})}></i>
                    {backdropOpacity <= 0.55 && from.customKey}
                </ButtonV2>
                {backdropOpacity > 0.55 && <div className="serie-details__top-header-title">{movie.title}</div>}
            </div>
            <div style={{
                position: "relative",
                zIndex: 2,
                width: "100%",
            }}>
                <div className="serie-details__header" style={{
                    height: "fit-content",
                }}>
                    {logoImage &&
                        <TmdbImage className="serie-details__header--logo" src={logoImage.file_path}/>}
                    {!logoImage && <div className="serie-details__header--title-placeholder">
                        {movie.title}</div>}
                    <div className="serie-details__header--categories">
                        {movie.genres?.map((genre, index) => (
                            <span className="serie-details__header--categories-item" key={genre.id}>
                                {genre.name}
                            </span>
                        ))}
                        <span className="serie-details__header--categories-item">
                                {movie.vote_average.toFixed(1)} <i className="fa-solid fa-star fa-xs"></i>
                            </span>
                    </div>
                    <div className="serie-details__header--info text-muted">
                        <span>{getYearOfDate(movie.release_date)}</span>
                        &nbsp;· {movie.runtime} min
                    </div>
                    <div className="serie-details__header--overview">
                        {movie.overview}
                    </div>
                    <div className="serie-details__header--actions">
                        <ButtonV2 className="serie-details__header--actions-play"
                                  onClick={() => {
                                      navigate("play")
                                  }}>
                            <i className="fa-solid fa-play pe-3 fa-lg"></i>Ver
                        </ButtonV2>
                        <TmdbCollectionSelector item={movie} isMovie={true}/>
                        <TmdbViewLater tmdbItem={movie}></TmdbViewLater>
                        <TmdbLikeItem tmdbItem={movie}/>
                        <TmdbViewToggle item={movie}/>
                    </div>
                </div>
                <div className="serie-details__content">
                    <div className="serie-details__content--routes">
                        {
                            routes.map((route) => (
                                <button
                                    onClick={() => route.route ? setSelectedRoute(route.route) : route.onClick?.()}
                                    key={route.route ?? route.name}
                                    className={cn("serie-details__content--routes-item", {
                                        "serie-details__content--routes-item--active": route.route === selectedRoute
                                    })}>
                                    {route.name}
                                </button>
                            ))

                        }
                    </div>
                    {selectedRoute === "suggestions" && <div>
                        <TmdbItemSuggestions item={movie} isMovie={true}/>
                    </div>}
                    {selectedRoute === "details" &&
                        <div>
                            <div className="serie-details__header--title-placeholder w-100 mt-4">
                                {movie.title}
                            </div>
                            <div className="row mt-3 mb-3">
                                <div className="col-md-6 pt-4">
                                    {movie.overview}
                                </div>
                                <div className="col-md-6">
                                    <TmdbScore score={movie.vote_average}></TmdbScore>
                                    {movie.view_date &&
                                        <center className="pt-4">
                                            <small className="text-muted">
                                                <i className="fa-regular fa-eye"></i> {getViewMessage(intl, movie.view_date)}
                                            </small>
                                        </center>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                    {selectedRoute === "reviews" &&
                        <div className="mt-4 mb-3">
                            <TmdbItemReviews item={movie} isMovie={true}/>
                        </div>
                    }
                </div>
            </div>
            <div
                className="serie-details__background-image"
                style={{
                    width: parentRef.width - 2,
                    height: parentRef.width * backgroundAspectRatio,
                }}>
                <div className="serie-details__background-image--backdrop" style={{
                    opacity: backdropOpacity,
                }}></div>
                <div className="serie-details__background-image--overlay"></div>
                {backgroundImage && <TmdbImage
                    className="serie-details__background-image--still"
                    src={backgroundImage.file_path} size={"original"}/>}
            </div>
            <Routes>
                <Route path="play"
                       element={
                           <div style={{
                               position: "fixed",
                               top: 0,
                               left: 0,
                               width: "100%",
                               height: "100%",
                               backgroundColor: darkTheme ? "black" : "white",
                               zIndex: 12,
                           }}>
                               <OverviewCardV2
                                   view={true} imagePath={movie.backdrop_path} file={movie.shared_files[0]}
                                   overview={movie.overview}
                                   item={movie}
                                   playing={playing}
                                   type={"movie"}
                                   squadFlixTvHook={squadflixTvHook}
                                   tmdbId={movie.id}/>
                           </div>
                       }/>
                <Route path="*" element={
                    <div style={{
                        display: 'none'
                    }}>
                        <TmdbNotepad tmdbItem={movie}/> {/* Notas  NO BORRAR*/}
                    </div>
                }/>
            </Routes>
            {false && <div style={{display: 'none'}}>
                <div className="serie-details__title" style={{
                    color: darkTheme ? "white" : "black",
                }}>
                    {movie.title}
                </div>


                <TmdbScore score={movie.vote_average}/>
            </div>}

        </div>
    )
}

export default MovieDetails;
